import {
  defaultSalesCategoryHierarchy,
  isReferenceKey,
  levelsInHierarchyBySalesCategory,
  prettifyPrimaryLevelName,
  prettifySalesCategory,
  prettifySecondaryLevelName,
  referenceKeyPrefix,
  salesCategoryNamesInHierarchy,
  sanitizeSalesCategory,
  specialSalesCategories,
} from "api-types/builders/salesCategory";
import type {
  BuilderSalesCategoryCustomization,
  OpsCategoryTree,
  SalesCategoryHierarchy,
  SalesCategoryHierarchyPrimaryLevel,
  SalesCategoryHierarchySecondaryLevel,
  SalesCategorySizes,
  SalesCategoryTree,
} from "api-types/builders/salesCategoryTypes";
import type { Builder } from "api-types/modelTypes";
import type { Doc } from "building-types/doc";
import type {
  SalesCategoryPrimaryLevel,
  SalesCategoryPrimaryLevelIcon,
  SalesCategorySecondaryLevel,
} from "common/types/salesCategoryTypesEnum";
import { SalesCategory } from "common/types/salesCategoryTypesEnum";
import objectIsEmpty from "common/utils/objectIsEmpty";
import { sortTableBySortIndex } from "common/utils/sortIndex";
import uid from "common/utils/uid";
import { isIntersectingOptionSet } from "doc/options/optionChoicesTools";
import { isBooleanOptionSet } from "doc/options/optionSetBaseUtils";
import { optionSetIsDisabled } from "doc/options/optionSetTools";
import { isExteriorPaletteOptionSet } from "doc/options/paletteOptionSet";
import { isElevationOptionSet } from "doc/options/systemOptionSet";

export function cloneDefaultSalesCategoryHierarchy() {
  return JSON.parse(JSON.stringify(defaultSalesCategoryHierarchy));
}

function getReferenceKeys(hierarchy: SalesCategoryHierarchy): string[] {
  if (!hierarchy) {
    return [];
  }

  const referenceKeys: string[] = [];
  for (const primaryLevel of hierarchy.primaryLevels) {
    if (isReferenceKey(primaryLevel.key)) {
      referenceKeys.push(primaryLevel.key);
    }

    for (const secondaryLevel of primaryLevel.secondaryLevels) {
      if (isReferenceKey(secondaryLevel.key)) {
        referenceKeys.push(secondaryLevel.key);
      }
    }
  }
  return referenceKeys;
}

export function createReferenceKeys(hierarchy: SalesCategoryHierarchy, count: number): string[] {
  const currentKeys = getReferenceKeys(hierarchy);
  const newKeys: string[] = [];
  while (newKeys.length < count) {
    const newKey = `${referenceKeyPrefix}${uid(4)}`;
    if (!currentKeys.includes(newKey)) {
      newKeys.push(newKey);
    }
  }
  return newKeys;
}

export function primaryLevelInHierarchyByPrimary(
  key: string,
  hierarchy: SalesCategoryHierarchy,
): SalesCategoryHierarchyPrimaryLevel {
  const primaryLevels = hierarchy?.primaryLevels ?? defaultSalesCategoryHierarchy.primaryLevels;
  return primaryLevels.find((primaryLevel) => primaryLevel.key === key) ?? { key, secondaryLevels: [] };
}

export function secondaryLevelsInHierarchyByPrimary(
  key: string,
  hierarchy: SalesCategoryHierarchy,
): SalesCategoryHierarchySecondaryLevel[] {
  return primaryLevelInHierarchyByPrimary(key, hierarchy).secondaryLevels;
}

export function levelsInHierarchyByPrimaryAndSecondary(
  primary: string,
  secondary: string,
  hierarchy: SalesCategoryHierarchy,
): { primaryLevel: SalesCategoryHierarchyPrimaryLevel; secondaryLevel: SalesCategoryHierarchySecondaryLevel } {
  const primaryLevels = hierarchy?.primaryLevels ?? defaultSalesCategoryHierarchy.primaryLevels;
  for (const primaryLevel of primaryLevels) {
    if (primaryLevel.key === primary) {
      const secondaryLevel = primaryLevel.secondaryLevels.find((secondaryLevel) => secondaryLevel.key === secondary);
      if (secondaryLevel) {
        return { primaryLevel, secondaryLevel };
      }
    }
  }

  // Showroom calls this method on non-targetable hierarchy elements, so we handle it here
  return {
    primaryLevel: { key: primary, secondaryLevels: [] },
    secondaryLevel: { key: secondary, salesCategory: "" },
  };
}

const defaultSalesCategoryLevels: Record<
  SalesCategory,
  [SalesCategoryPrimaryLevel, SalesCategorySecondaryLevel | null]
> = {
  UNCATEGORIZED: ["UNCATEGORIZED", null],
  OPERATIONS: ["OPERATIONS", null],
  EXTRA_OPTIONS: ["EXTRA_OPTIONS", null],

  MISC_GENERAL: ["MISC", "GENERAL"],

  DESIGN_ELEVATIONS: ["DESIGN", "ELEVATIONS"],
  DESIGN_PALETTES: ["DESIGN", "PALETTES"],
  DESIGN_FOUNDATION: ["DESIGN", "FOUNDATION"],

  BED_BATH_GENERAL: ["BED_BATH", "GENERAL"],
  BED_BATH_OWNERS_SUITE: ["BED_BATH", "OWNERS_SUITE"],
  BED_BATH_SECONDARY_BATH: ["BED_BATH", "SECONDARY_BATH"],
  BED_BATH_SECONDARY_BED: ["BED_BATH", "SECONDARY_BED"],

  ENTRY_GENERAL: ["ENTRY", "GENERAL"],
  ENTRY_PORCH: ["ENTRY", "PORCH"],
  ENTRY_OWNERS_ENTRY: ["ENTRY", "OWNERS_ENTRY"],
  ENTRY_STAIRS: ["ENTRY", "STAIRS"],

  KITCHEN_GENERAL: ["KITCHEN", "GENERAL"],
  KITCHEN_CABINETS: ["KITCHEN", "CABINETS"],
  KITCHEN_APPLIANCES: ["KITCHEN", "APPLIANCES"],

  LIVING_GENERAL: ["LIVING", "GENERAL"],
  LIVING_FLEXIBLE: ["LIVING", "FLEXIBLE"],
  LIVING_DINING: ["LIVING", "DINING"],
  LIVING_HALL: ["LIVING", "HALL"],

  OUTDOOR_GENERAL: ["OUTDOOR", "GENERAL"],
  OUTDOOR_LIVING: ["OUTDOOR", "LIVING"],
  OUTDOOR_PATIO: ["OUTDOOR", "PATIO"],

  UTILITY_GENERAL: ["UTILITY", "GENERAL"],
  UTILITY_GARAGE: ["UTILITY", "GARAGE"],
  UTILITY_FLEXIBLE: ["UTILITY", "FLEXIBLE"],
  UTILITY_LAUNDRY: ["UTILITY", "LAUNDRY"],
};

export const SALES_CATEGORIES: SalesCategory[] = Object.values(SalesCategory);
export const SALES_CATEGORY_PRIMARY_LEVELS: SalesCategoryPrimaryLevel[] = Array.from(
  new Set(SALES_CATEGORIES.map((c) => defaultSalesCategoryLevels[c][0])),
);

export const SALES_CATEGORY_DEFAULT_PRIMARY_LEVEL_ORDER: SalesCategoryPrimaryLevel[] = [
  "DESIGN",
  "ENTRY",
  "LIVING",
  "KITCHEN",
  "BED_BATH",
  "OUTDOOR",
  "UTILITY",
  "MISC",
];

export function primaryIcon(primaryLevel: SalesCategoryHierarchyPrimaryLevel): SalesCategoryPrimaryLevelIcon | null {
  if (!primaryLevel) {
    return null;
  }

  const { key: primary } = primaryLevel;
  if (isReferenceKey(primary)) {
    return primaryLevel.icon ?? null;
  } else {
    return primaryLevel.icon ?? (primary as SalesCategoryPrimaryLevelIcon);
  }
}

function buildEmptyTree(hierarchy: SalesCategoryHierarchy): SalesCategoryTree {
  const tree = {};
  for (const primaryLevel of hierarchy.primaryLevels) {
    tree[primaryLevel.key] = {};

    for (const secondaryLevel of primaryLevel.secondaryLevels) {
      tree[primaryLevel.key][secondaryLevel.key] = {};
      if (secondaryLevel.salesCategory === SalesCategory.DESIGN_ELEVATIONS) {
        tree[primaryLevel.key][secondaryLevel.key].elevations = [];
      } else if (secondaryLevel.salesCategory === SalesCategory.DESIGN_PALETTES) {
        tree[primaryLevel.key][secondaryLevel.key].palettes = [];
      }
    }
  }

  // Extra options always go last
  tree["EXTRA_OPTIONS"] = { GENERAL: {} };

  return tree as SalesCategoryTree;
}

function primaryAndSecondaryKeysBySalesCategory(
  salesCategory: string,
  hierarchy: SalesCategoryHierarchy,
): { primary: string; secondary: string } {
  if (!salesCategory || salesCategory === SalesCategory.OPERATIONS) {
    return { primary: "", secondary: "" };
  }
  if (salesCategory === SalesCategory.UNCATEGORIZED) {
    return { primary: "MISC", secondary: "GENERAL" };
  }
  if (salesCategory === SalesCategory.EXTRA_OPTIONS) {
    return { primary: "EXTRA_OPTIONS", secondary: "GENERAL" };
  }
  const { primaryLevel, secondaryLevel } = levelsInHierarchyBySalesCategory(salesCategory, hierarchy);
  if (primaryLevel === null || secondaryLevel === null) {
    return { primary: "MISC", secondary: "GENERAL" };
  }

  return { primary: primaryLevel.key, secondary: secondaryLevel.key };
}

/**
 * check if Design is empty - SalesCategory check for empty categories is shallow
 */
export function removeDesignCategoryIfEmpty(tree: SalesCategoryTree) {
  // remove empty design groups
  const designCat = tree.DESIGN;

  for (const optionSetId in designCat) {
    Object.getOwnPropertyNames(designCat[optionSetId]).forEach((prop) => {
      if (designCat[optionSetId][prop].length === 0) {
        delete designCat[optionSetId][prop];
      }
    });
    if (objectIsEmpty(designCat[optionSetId])) {
      delete designCat[optionSetId];
    }
  }
  if (objectIsEmpty(designCat)) {
    delete tree.DESIGN;
  }

  return tree;
}

/**
 * Normalize structure of sales categories found in paramSet.
 *
 * This is currently only used in Showroom
 */
export function salesCategoryTreeNew(
  optionSets: Doc["optionSets"],
  hierarchy: SalesCategoryHierarchy,
): SalesCategoryTree {
  const tree: SalesCategoryTree = buildEmptyTree(hierarchy);

  for (const optionSet of sortTableBySortIndex(optionSets)) {
    if (optionSetIsDisabled(optionSets[optionSet.id])) {
      continue;
    }

    if (isIntersectingOptionSet(optionSet)) {
      continue;
    }

    let salesCategory = optionSets[optionSet.id]?.salesCategory;
    const isElevation = isElevationOptionSet(optionSet.id);
    const listType = isBooleanOptionSet(optionSets[optionSet.id]) ? "upgrades" : "options";
    const isExteriorPalette = isExteriorPaletteOptionSet(optionSets[optionSet.id]);

    // NOTE: "elevations" and "palettes" do not have a "salesCategory" property.
    // they are a special case w/diff data structure and template requirements.
    if (isElevation && !isExteriorPalette) {
      tree.DESIGN?.ELEVATIONS.elevations.push(optionSet.id);
    } else if (isExteriorPalette) {
      tree.DESIGN?.PALETTES.palettes.push(optionSet.id);
    } else if (salesCategory) {
      if (salesCategory === SalesCategory.OPERATIONS) {
        continue;
      }

      // Determine final navigation levels in Showroom
      salesCategory = sanitizeSalesCategory(salesCategory, hierarchy);
      const { primary, secondary } = primaryAndSecondaryKeysBySalesCategory(salesCategory, hierarchy);

      // Add to appropriate secondary level
      if (!tree[primary][secondary]) {
        tree[primary][secondary] = { [listType]: [optionSet.id] };
      } else {
        if (tree[primary][secondary][listType]) {
          tree[primary][secondary][listType].push(optionSet.id);
        } else {
          tree[primary][secondary][listType] = [optionSet.id];
        }
      }
    }
  }

  // Remove empty levels
  for (const primaryLevelKey of Object.keys(tree)) {
    for (const secondaryLevelKey of Object.keys(tree[primaryLevelKey])) {
      if (objectIsEmpty(tree[primaryLevelKey][secondaryLevelKey])) {
        delete tree[primaryLevelKey][secondaryLevelKey];
      }
    }

    if (objectIsEmpty(tree[primaryLevelKey])) {
      delete tree[primaryLevelKey];
    }
  }

  return tree;
}

/**
 * Returns flat list of optionSetIds in the order they appear in the sales category tree.
 * @param optionSets
 * @param hierarchy
 * @returns array of optionSetIds
 */

export function salesCategoryFlatList(optionSets: Doc["optionSets"], hierarchy: SalesCategoryHierarchy): string[] {
  const tree = salesCategoryTreeNew(optionSets, hierarchy);
  const flatList: string[] = [];

  for (const primaryLevelKey in tree) {
    for (const secondaryLevelKey in tree[primaryLevelKey]) {
      const secondaryLevel = tree[primaryLevelKey][secondaryLevelKey];
      for (const listType in secondaryLevel) {
        flatList.push(...secondaryLevel[listType]);
      }
    }
  }

  return flatList;
}

/**
 * Maps each optionSetId to its "Primary - Secondary" sales-category display label, matching the
 * brochure/showroom sort order. Option sets are omitted (callers treat a missing entry as "no
 * header") when they have no sales category, are operations, or sit under an unnamed custom level.
 */
export function buildOptionSetSalesCategoryLabels(
  optionSets: Doc["optionSets"],
  hierarchy: SalesCategoryHierarchy,
): Map<string, string> {
  const tree = salesCategoryTreeNew(optionSets, hierarchy);
  const primaryLevels = hierarchy?.primaryLevels ?? defaultSalesCategoryHierarchy.primaryLevels;
  const labels = new Map<string, string>();

  for (const primaryLevel of primaryLevels) {
    const secondaryByKey = tree[primaryLevel.key];
    if (!secondaryByKey) {
      continue;
    }
    const primaryName = prettifyPrimaryLevelName(primaryLevel);
    if (primaryName === undefined) {
      continue;
    }
    for (const secondaryLevel of primaryLevel.secondaryLevels) {
      const group = secondaryByKey[secondaryLevel.key];
      if (!group) {
        continue;
      }
      const secondaryName = prettifySecondaryLevelName(secondaryLevel);
      if (secondaryName === undefined) {
        continue;
      }
      const label = `${primaryName} - ${secondaryName}`;
      for (const optionSetId of Object.values(group).flat()) {
        labels.set(optionSetId, label);
      }
    }
  }

  return labels;
}

/**
 * Normalize structure of ops categories found in option sets.
 *
 * This is currently only used in the Showroom
 */
export function opsCategoryTreeNew(optionSets: Doc["optionSets"], hierarchy: SalesCategoryHierarchy): OpsCategoryTree {
  const tree: OpsCategoryTree = {
    ELEVATIONS: { elevations: [] },
    PALETTES: { palettes: [] },
  };
  for (const optionSetId in optionSets) {
    if (optionSetIsDisabled(optionSets[optionSetId])) {
      continue;
    }

    if (isIntersectingOptionSet(optionSets[optionSetId])) {
      continue;
    }

    let category = optionSets[optionSetId]?.category ?? "Uncategorized";

    const listType = isBooleanOptionSet(optionSets[optionSetId]) ? "upgrades" : "options";
    const isExteriorPalette = isExteriorPaletteOptionSet(optionSets[optionSetId]);

    if (isElevationOptionSet(optionSetId) && !isExteriorPalette) {
      tree.ELEVATIONS?.elevations.push(optionSetId);
    } else if (isExteriorPalette) {
      tree.PALETTES?.palettes.push(optionSetId);
    } else {
      const salesCategory = sanitizeSalesCategory(optionSets[optionSetId]?.salesCategory ?? "", hierarchy);

      const { secondary } = primaryAndSecondaryKeysBySalesCategory(salesCategory, hierarchy);

      if (secondary === "ELEVATIONS") {
        category = "ELEVATIONS";
      }

      if (!tree[category]) {
        tree[category] = { [listType]: [optionSetId] };
      } else {
        if (tree[category][listType]) {
          tree[category][listType].push(optionSetId);
        } else {
          tree[category][listType] = [optionSetId];
        }
      }
    }
  }

  // Remove empty levels
  for (const primaryLevelKey of Object.keys(tree)) {
    if (objectIsEmpty(tree[primaryLevelKey])) {
      delete tree[primaryLevelKey];
    }
  }
  if (tree.ELEVATIONS?.elevations.length === 0) {
    delete tree.ELEVATIONS;
  }
  if (tree.PALETTES?.palettes.length === 0) {
    delete tree.PALETTES;
  }

  return tree;
}

export function salesCategorySelectItems(customization: BuilderSalesCategoryCustomization | undefined) {
  const salesCategories = salesCategoryNamesInHierarchy(customization?.hierarchy, true);
  return salesCategories.map((salesCategory) => ({
    label: prettifySalesCategory(salesCategory, customization) ?? "Unlabeled",
    value: salesCategory,
  }));
}

export function filterSalesCategories(
  builder: Builder,
  salesCategorySizes: SalesCategorySizes,
  hideEmptySalesCategories: boolean,
): string[] {
  const salesCategories = salesCategoryNamesInHierarchy(
    builder.salesConfig?.salesCategoryCustomization?.hierarchy,
    true,
  );

  let filteredSalesCategories = [...salesCategories];

  if (hideEmptySalesCategories) {
    filteredSalesCategories = filteredSalesCategories.filter((salesCategory) => salesCategorySizes[salesCategory] > 0);
  }

  return filteredSalesCategories;
}

/**
 * Creates a mapping between the secondary sales categories to its primary category.
 *
 * Appends default sales categories to the mapping if includeSpecial is true.
 *
 * Uses primary pretty name as default, key as alt
 * @param customSalesCategoryHierarchy
 * @returns
 */
export function getSecondaryToPrimaryNameMapping(
  customSalesCategoryHierarchy: SalesCategoryHierarchy | undefined,
  includeSpecial: boolean = false,
): Record<string, string> {
  const nameMap: Record<string, string> = {};
  if (includeSpecial) {
    for (const specialCategory of specialSalesCategories as string[]) {
      nameMap[specialCategory] = specialCategory;
    }
  }
  // Always populate the hierarchy mappings - use custom if available, otherwise use default
  const primaryLevels = customSalesCategoryHierarchy?.primaryLevels ?? defaultSalesCategoryHierarchy.primaryLevels;
  for (const primaryLevel of primaryLevels) {
    const primaryCategoryKey = primaryLevel.key;
    for (const secondaryLevel of primaryLevel.secondaryLevels) {
      nameMap[secondaryLevel.salesCategory] = primaryCategoryKey;
    }
  }
  return nameMap;
}

/**
 * Creates a mapping from the level `key` value to the name to be used everywhere else.
 * This should be the `prettyName` but will default to `key` if it's not defined.
 * @param customHierarchyPrimaryLevels Array of primary level data
 * @param includeSpecial Flag to include special categories, will do so before the custom data
 * @returns
 */
export function getPrimarySalesKeyToNameMapping(
  customHierarchyPrimaryLevels: SalesCategoryHierarchyPrimaryLevel[] | undefined,
  includeSpecial: boolean = false,
): Record<string, string> {
  const keyNameMap: Record<string, string> = {};
  if (includeSpecial) {
    for (const specialCategory of specialSalesCategories as string[]) {
      keyNameMap[specialCategory] = specialCategory;
    }
  }

  if (customHierarchyPrimaryLevels) {
    customHierarchyPrimaryLevels.forEach((level) => {
      keyNameMap[level.key] = level.prettyName ?? level.key;
    });
  } else {
    // Use default primary levels if no custom hierarchy loaded
    defaultSalesCategoryHierarchy.primaryLevels.forEach((level) => {
      keyNameMap[level.key] = level.prettyName ?? level.key;
    });
  }

  return keyNameMap;
}

/**
 * Return the total sub-categories under each primary sales category.
 *
 * @param secondarySalesCategories # key to the category counts data, also the sales category values
 * @param secondarySalesCategoryCounts # of categories in each secondary sales category, secondaryLevel salesCategory is used for lookup
 * @param customSalesCategoryHierarchy contains all sales categories data
 * @returns map of primary category name to sub-category count
 */
export function getPrimarySalesCategoryCounts(
  secondarySalesCategories: string[],
  secondarySalesCategoryCounts: SalesCategorySizes,
  customSalesCategoryHierarchy: SalesCategoryHierarchy | undefined,
): Record<string, number> {
  const primarySalesCategoryCounts: Record<string, number> = {};

  // Find the primary level using the secondary level category name, use default hierarchy primary levels if no custom hierarchy loaded:
  const primaryLevels = customSalesCategoryHierarchy?.primaryLevels ?? defaultSalesCategoryHierarchy.primaryLevels;
  for (const secondaryCategoryName of secondarySalesCategories) {
    let primaryCategoryKey: string = "";

    const primaryLevel: SalesCategoryHierarchyPrimaryLevel | undefined = primaryLevels.find((primaryLevel) =>
      primaryLevel.secondaryLevels.find((secondaryLevel) => secondaryLevel.salesCategory === secondaryCategoryName),
    );

    // Use the primary level key if primary level is found, otherwise use the secondary category name as a fallback
    // Assuming that this is a special category that won't have a primary level, but will have an entry in the secondarySalesCategory list
    primaryCategoryKey = primaryLevel ? primaryLevel.key : secondaryCategoryName;

    if (primarySalesCategoryCounts[primaryCategoryKey] === undefined) {
      primarySalesCategoryCounts[primaryCategoryKey] = 0;
    }

    primarySalesCategoryCounts[primaryCategoryKey] += secondarySalesCategoryCounts[secondaryCategoryName] ?? 0;
  }

  return primarySalesCategoryCounts;
}
